import React, {useState} from "react";
import {ActionResponse, QuoteStatus, WorkOrder, WorkOrderActions, WorkOrderStatus} from "../types";
import {Button, ButtonGroup} from "react-bootstrap";
import {Spinner} from "react-bootstrap";
import {getWorkOrderPDF, postWOAction} from "../api/dataService";
import {Row} from "@tanstack/react-table";
import {ACTION_IN_FLIGHT_MESSAGE, beginAction, endAction, UNKNOWN_ACTION_FAILURE} from "../util/ActionGuard";
import {handlePostError} from "../util/Helpers";
import ActionErrorModal from "./ActionErrorModal";

interface WorkOrderActionButtonProps {
    workorder:  Row<WorkOrder>,
    onUpdate?: (updatedObject: WorkOrder, previousObject: Row<WorkOrder>)  => void
}

const WorkOrderActionButtons: React.FC<WorkOrderActionButtonProps> = (props: WorkOrderActionButtonProps) => {

    const [isLoading, setLoading] = useState<boolean>(false)
    const [errorMessages, setErrorMessages] = useState<string[]>([])

    const handleWorkOrderAction = async (workorder_id: string, action: WorkOrderActions) => {
        if(action === WorkOrderActions.save) {
            try {
                setLoading(true)
                await getWorkOrderPDF(workorder_id)
            } catch (error) {
                setErrorMessages([UNKNOWN_ACTION_FAILURE])
            } finally {
                setLoading(false)
            }
            return
        }

        // Survives the remount that paging away and back causes, unlike isLoading
        if(!beginAction(workorder_id, action)) {
            setErrorMessages([ACTION_IN_FLIGHT_MESSAGE])
            return
        }

        try {
            setLoading(true)
            const response = await postWOAction(workorder_id, action) as ActionResponse

            if (response && response.success && response.workorder) {
                if(props.onUpdate) {
                    props.onUpdate(response.workorder, props.workorder)
                }
            } else {
                // The server refused the change and explains why
                setErrorMessages([response?.message || UNKNOWN_ACTION_FAILURE])
            }
        } catch (error) {
            const messages = handlePostError(error, 'WorkOrderAction')
            setErrorMessages(messages.length ? messages : [UNKNOWN_ACTION_FAILURE])
        } finally {
            setLoading(false)
            endAction(workorder_id, action)
        }
    }

    let buttonsToRender;

    switch (props.workorder.original.workorder_status) {
        case WorkOrderStatus.open:
            buttonsToRender = (
                <div className="d-flex justify-content-center">
                    <ButtonGroup className="btn-group-sm">
                        <Button onClick={() => handleWorkOrderAction(props.workorder.original.id, WorkOrderActions.activated)} className="btn-success" disabled={isLoading}>Activate</Button>
                    </ButtonGroup>
                </div>
            );
            break;

        case WorkOrderStatus.activated:
            buttonsToRender = (
                <div className="d-flex justify-content-center">
                    <ButtonGroup className="btn-group-sm">
                        <Button className="btn-warning" style={{whiteSpace: 'nowrap'}} onClick={() => handleWorkOrderAction(props.workorder.original.id, WorkOrderActions.closed)} disabled={isLoading}> Prep Invoice</Button>
                    </ButtonGroup>
                </div>
            )
            break;

        case WorkOrderStatus.closed:
            buttonsToRender = (
                <div className="d-flex justify-content-center">
                    <ButtonGroup className="btn-group-sm">
                        <Button className="btn-warning" onClick={() => handleWorkOrderAction(props.workorder.original.id,WorkOrderActions.invoice)} disabled={isLoading}>Invoice</Button>
                    </ButtonGroup>
                </div>
            )
            break;

        case WorkOrderStatus.complete:
            buttonsToRender = (            <ButtonGroup className="btn-group-sm">
                <Button style={{whiteSpace: 'nowrap'}} onClick={() => handleWorkOrderAction(props.workorder.original.id,WorkOrderActions.save)}>Download PDF</Button></ButtonGroup>)
            break;
        default:
            buttonsToRender = (
                <div className="d-flex justify-content-center">
                    No Actions
                </div>
            )
    }
    return (
        <>
            {isLoading
                ? <center><Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner></center>
                : buttonsToRender}
            <ActionErrorModal
                show={errorMessages.length > 0}
                onHide={() => setErrorMessages([])}
                messages={errorMessages}
            />
        </>
    )

    }

export default WorkOrderActionButtons;